import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AircraftMetadata } from "@/pages/setup/AircraftMetadata";
import { APM } from "@/pages/setup/APM";
import { BaggageCarousels } from "@/pages/setup/BaggageCarousels";
import { LandingFeeExceptions } from "@/pages/setup/LandingFeeExceptions";
import { SecurityLanes } from "@/pages/setup/SecurityLanes";
import { useAirportDetails } from "@/queries/airport";
import { useSessionData } from "@/stores/SessionDataContext";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const VALID_TABS = [
  "general",
  "exceptions",
  "metadata",
  "security",
  "baggage",
  "apm",
] as const;
type ValidTab = (typeof VALID_TABS)[number];
const DEFAULT_TAB: ValidTab = "security";

// --- Component ---
function ManageAirportPage() {
  // --- Hooks ---
  const { data: airportData } = useAirportDetails();
  const { airport } = useSessionData();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine which tabs should be available based on landing fees feature and staff status
  const showLandingFeeTabs = airport.features.landing_fees;

  // Get available tabs based on permissions
  const getAvailableTabs = (): ValidTab[] => {
    const tabs: ValidTab[] = ["general", "security", "baggage", "apm"];
    if (showLandingFeeTabs) {
      tabs.push("exceptions", "metadata");
    }
    return tabs;
  };

  const [activeTab, setActiveTab] = useState<ValidTab>(() => {
    const tabParam = searchParams.get("tab");
    const availableTabs = getAvailableTabs();
    if (tabParam && availableTabs.includes(tabParam as ValidTab)) {
      return tabParam as ValidTab;
    }
    return DEFAULT_TAB;
  });

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const availableTabs = getAvailableTabs();
    let canonicalTab: ValidTab = DEFAULT_TAB;
    let urlNeedsNormalization = false;

    if (tabParam && availableTabs.includes(tabParam as ValidTab)) {
      canonicalTab = tabParam as ValidTab;
    } else {
      urlNeedsNormalization = true;
    }

    if (activeTab !== canonicalTab) {
      setActiveTab(canonicalTab);
    }

    if (urlNeedsNormalization || searchParams.get("tab") !== canonicalTab) {
      setSearchParams({ tab: canonicalTab }, { replace: true });
    }
  }, [searchParams, activeTab, setSearchParams, showLandingFeeTabs]);

  const handleTabChange = (newTabValue: string) => {
    const availableTabs = getAvailableTabs();
    if (availableTabs.includes(newTabValue as ValidTab)) {
      setSearchParams({ tab: newTabValue as ValidTab });
    }
  };

  // --- Render Logic ---
  if (!airportData) {
    return <div className="p-4">Loading airport data...</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <h1 className="text-2xl font-semibold">
        Airport Settings ({airportData.code_iata})
      </h1>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList
          className={`grid w-full ${showLandingFeeTabs ? "grid-cols-5" : "grid-cols-3"}`}
        >
          {/* <TabsTrigger value="general">General Settings</TabsTrigger> */}
          <TabsTrigger value="security">Security Lanes</TabsTrigger>
          <TabsTrigger value="baggage">Baggage Claim</TabsTrigger>
          <TabsTrigger value="apm">APM</TabsTrigger>
          {showLandingFeeTabs && (
            <>
              <TabsTrigger value="exceptions">
                Landing Fee Exceptions
              </TabsTrigger>
              <TabsTrigger value="metadata">Tail Numbers</TabsTrigger>
            </>
          )}
        </TabsList>

        {/* General Settings Tab */}
        {/* <TabsContent value="general">
          <ConnectingFactorSettings airportData={airportData} />
        </TabsContent> */}

        {/* Security Lanes Tab */}
        <TabsContent value="security">
          <SecurityLanes />
        </TabsContent>

        {/* Baggage Claim Tab */}
        <TabsContent value="baggage">
          <BaggageCarousels />
        </TabsContent>

        {/* APM Tab */}
        <TabsContent value="apm">
          <APM />
        </TabsContent>

        {/* Landing Fee Exceptions Tab */}
        {showLandingFeeTabs && (
          <TabsContent value="exceptions">
            <LandingFeeExceptions />
          </TabsContent>
        )}

        {/* Aircraft Metadata Tab */}
        {showLandingFeeTabs && (
          <TabsContent value="metadata">
            <AircraftMetadata />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export default ManageAirportPage;
